import { Fragment, Suspense, lazy, useEffect, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { NomenclatorEntry } from '@lyra/shared';
import { DocTop } from '@/components/shell/DocTop';
import { Pencil, Trash2, ChevronLeft, ChevronRight, Download, Upload, Plus, X, Search } from 'lucide-react';
// The import panel pulls the heavy file libraries (ExcelJS + PapaParse via
// lib/parse). It only mounts when the operator opens „Importă", so lazy-load it
// to keep those libs off the catalog page's initial load.
const NomenclatorImport = lazy(() =>
  import('./NomenclatorImport').then((m) => ({ default: m.NomenclatorImport })),
);
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/ui/Pill';
import { formatDateRo, formatNumberRo } from '@/lib/format';
import { useMe } from '@/lib/useMe';
import { canEditNomenclator } from '@/lib/permissions';
import { ApiClientError } from '@/lib/apiClient';
import { fieldAria } from '@/lib/formA11y';
import {
  useNomenclator,
  useCreateNomenclator,
  useUpdateNomenclator,
  useDeleteNomenclator,
  useExportNomenclator,
} from './api/nomenclatorHooks';
import { downloadNomenclatorCsv } from './lib/exportNomenclator';

const PAGE_SIZES = [25, 50, 100] as const;
const selectClass = 'h-9 rounded-md border border-rule bg-transparent px-2 text-[13.5px] text-ink-2';
const filterLabelClass = 'text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3';

const entrySchema = z.object({
  codBdMedoc: z.string().trim().min(1, 'Cod BD MEDOC este obligatoriu.'),
  codMedoc: z.string().trim().min(1, 'Cod MEDOC este obligatoriu.'),
  denumireMedoc: z.string().trim().min(1, 'Denumire MEDOC este obligatorie.'),
});
type EntryFields = z.infer<typeof entrySchema>;

const DUPLICATE_MESSAGE = 'Există deja o înregistrare identică.';

/** Surfaces the server's 409 (UNIQUE-constraint collision, see
 *  `data.createNomenclatorEntry`/`updateNomenclatorEntry`) as the fixed RO
 *  duplicate message; any other error falls back to its own text. */
function messageFor(err: unknown): string {
  if (err instanceof ApiClientError) {
    return err.status === 409 ? DUPLICATE_MESSAGE : err.message;
  }
  return 'A apărut o eroare neașteptată.';
}

const TABLE_COL_COUNT_EDITABLE = 5;
const TABLE_COL_COUNT_READONLY = 4;

function RowSkeleton({ colCount }: { colCount: number }) {
  return (
    <tr className="border-b border-rule-soft">
      <td className="px-4 py-3" colSpan={colCount}>
        <div className="h-4 w-full animate-pulse rounded-md bg-wash" aria-hidden />
      </td>
    </tr>
  );
}

/** Inline (not modal) create form — Direction B. Collapses on success. */
function AddEntryForm({ onDone }: { onDone: () => void }) {
  const createMutation = useCreateNomenclator();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EntryFields>({
    resolver: zodResolver(entrySchema),
    defaultValues: { codBdMedoc: '', codMedoc: '', denumireMedoc: '' },
  });

  async function onSubmit(values: EntryFields) {
    setFormError(null);
    try {
      await createMutation.mutateAsync(values);
      reset();
      onDone();
    } catch (err) {
      setFormError(messageFor(err));
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-rule bg-wash px-4 py-4"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="add-cod-bd" className={filterLabelClass}>Cod BD MEDOC</Label>
        <Input id="add-cod-bd" autoFocus className="h-9 w-36 bg-bg font-mono" {...register('codBdMedoc')} {...fieldAria(!!errors.codBdMedoc, 'add-cod-bd-error')} />
        {errors.codBdMedoc && <p id="add-cod-bd-error" className="text-[12.5px] text-bad">{errors.codBdMedoc.message}</p>}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="add-cod-medoc" className={filterLabelClass}>Cod MEDOC</Label>
        <Input id="add-cod-medoc" className="h-9 w-36 bg-bg font-mono" {...register('codMedoc')} {...fieldAria(!!errors.codMedoc, 'add-cod-medoc-error')} />
        {errors.codMedoc && <p id="add-cod-medoc-error" className="text-[12.5px] text-bad">{errors.codMedoc.message}</p>}
      </div>
      <div className="flex min-w-[220px] flex-1 flex-col gap-1">
        <Label htmlFor="add-denumire" className={filterLabelClass}>Denumire MEDOC</Label>
        <Input id="add-denumire" className="h-9 bg-bg" {...register('denumireMedoc')} {...fieldAria(!!errors.denumireMedoc, 'add-denumire-error')} />
        {errors.denumireMedoc && <p id="add-denumire-error" className="text-[12.5px] text-bad">{errors.denumireMedoc.message}</p>}
      </div>
      <div className="flex items-center gap-3 pb-[1px]">
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? 'Se salvează…' : 'Salvează'}
        </Button>
        <button
          type="button"
          onClick={() => {
            reset();
            setFormError(null);
            onDone();
          }}
          className="text-[13.5px] text-ink-3 hover:text-ink-2"
        >
          Anulează
        </button>
      </div>
      {formError && <p className="w-full text-[12.5px] text-bad">{formError}</p>}
    </form>
  );
}

export function NomenclatorPage() {
  const { data: me } = useMe();
  const canEdit = canEditNomenclator(me);

  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Uncommitted filter inputs vs. applied filters — the query only refetches
  // once filters are applied (Enter / „Caută"), matching v1 `StoredView`'s
  // filter/applied split instead of firing a request on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [codBdInput, setCodBdInput] = useState('');
  const [codMedocInput, setCodMedocInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedCodBd, setAppliedCodBd] = useState('');
  const [appliedCodMedoc, setAppliedCodMedoc] = useState('');
  const hasAppliedFilters = Boolean(appliedSearch || appliedCodBd || appliedCodMedoc);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editFields, setEditFields] = useState({ codBdMedoc: '', codMedoc: '', denumireMedoc: '' });
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { data, isPending, isError } = useNomenclator({
    search: appliedSearch,
    codBdMedoc: appliedCodBd,
    codMedoc: appliedCodMedoc,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const updateMutation = useUpdateNomenclator();
  const deleteMutation = useDeleteNomenclator();
  const exportMutation = useExportNomenclator();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const colCount = canEdit ? TABLE_COL_COUNT_EDITABLE : TABLE_COL_COUNT_READONLY;

  // Deleting the last row on a non-first page (a failure mode v1 never had —
  // it shipped no delete) would otherwise strand `page` past `totalPages`,
  // showing a false "Nomenclatorul este gol." once the refetch settles.
  useEffect(() => {
    if (!isPending && !isError && total > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [isPending, isError, total, totalPages, page]);

  function applyFilters(e: FormEvent) {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setAppliedCodBd(codBdInput.trim());
    setAppliedCodMedoc(codMedocInput.trim());
    setPage(1);
  }

  function resetFilters() {
    setSearchInput('');
    setCodBdInput('');
    setCodMedocInput('');
    setAppliedSearch('');
    setAppliedCodBd('');
    setAppliedCodMedoc('');
    setPage(1);
  }

  function startEdit(entry: NomenclatorEntry) {
    setConfirmDeleteId(null);
    setEditingId(entry.id);
    setEditFields({ codBdMedoc: entry.codBdMedoc, codMedoc: entry.codMedoc, denumireMedoc: entry.denumireMedoc });
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError(null);
  }

  async function submitEdit(id: number) {
    const codBdMedoc = editFields.codBdMedoc.trim();
    const codMedoc = editFields.codMedoc.trim();
    const denumireMedoc = editFields.denumireMedoc.trim();
    if (!codBdMedoc || !codMedoc || !denumireMedoc) {
      setEditError('Toate câmpurile sunt obligatorii.');
      return;
    }
    setEditError(null);
    try {
      await updateMutation.mutateAsync({ id, codBdMedoc, codMedoc, denumireMedoc });
      setEditingId(null);
    } catch (err) {
      setEditError(messageFor(err));
    }
  }

  function handleEditKeyDown(e: KeyboardEvent<HTMLInputElement>, id: number) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submitEdit(id);
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }

  function handleExport() {
    // Export the WHOLE filtered catalog, not just the page on screen — an
    // operator asking to „Exportă" expects the full set the filters describe.
    // `mutate` (not `mutateAsync`) so the click handler never leaves a floating
    // rejected promise; the hook's onError toasts on failure.
    exportMutation.mutate(
      { search: appliedSearch, codBdMedoc: appliedCodBd, codMedoc: appliedCodMedoc },
      { onSuccess: (all) => { if (all.length > 0) downloadNomenclatorCsv(all, 'nomenclator.csv'); } }
    );
  }

  return (
    <>
      <DocTop />
      <div className="mt-8">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h1 className="text-[32px] font-[650] text-ink">Nomenclator</h1>
            <p className="mt-1 text-[13.5px] text-ink-3">
              {isPending ? 'Se încarcă…' : `${formatNumberRo(total)} înregistrări în nomenclator`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={total === 0 || exportMutation.isPending}>
              <Download />
              {exportMutation.isPending ? 'Se exportă…' : 'Exportă'}
            </Button>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setImportOpen((v) => !v); setAddOpen(false); }}
              >
                {importOpen ? <X /> : <Upload />}
                {importOpen ? 'Anulează importul' : 'Importă'}
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={() => { setAddOpen((v) => !v); setImportOpen(false); }}>
                {addOpen ? <X /> : <Plus />}
                {addOpen ? 'Anulează adăugarea' : 'Adaugă înregistrare'}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-1 border-b-2 border-ink pb-3" />

        {canEdit && importOpen && (
          <div className="mt-6">
            <Suspense fallback={null}>
              <NomenclatorImport onClose={() => setImportOpen(false)} existingCount={total} />
            </Suspense>
          </div>
        )}
        {canEdit && addOpen && <AddEntryForm onDone={() => setAddOpen(false)} />}

        {/* Toolbar: search + code filters, then a right-aligned page-size control. */}
        <form onSubmit={applyFilters} className="mt-6 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="nom-search" className={filterLabelClass}>Căutare</Label>
            <Input
              id="nom-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cod sau denumire…"
              className="h-9 w-56"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="nom-cod-bd" className={filterLabelClass}>Cod BD MEDOC</Label>
            <Input
              id="nom-cod-bd"
              value={codBdInput}
              onChange={(e) => setCodBdInput(e.target.value)}
              className="h-9 w-36 font-mono"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="nom-cod-medoc" className={filterLabelClass}>Cod MEDOC</Label>
            <Input
              id="nom-cod-medoc"
              value={codMedocInput}
              onChange={(e) => setCodMedocInput(e.target.value)}
              className="h-9 w-36 font-mono"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9"><Search />Caută</Button>
          {hasAppliedFilters && (
            <div className="flex items-center gap-2">
              <Pill variant="accent" dot={false}>Filtre active</Pill>
              <button
                type="button"
                onClick={resetFilters}
                className="text-[13.5px] text-ink-3 hover:text-ink-2 hover:underline"
              >
                Resetează filtrele
              </button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor="nom-page-size" className={filterLabelClass}>Rezultate/pagină</Label>
            <select
              id="nom-page-size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </form>

        <div className="mt-6">
          {isError && <p className="text-[13.5px] text-bad">Nu s-a putut încărca nomenclatorul.</p>}

          {!isError && isPending && (
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <table className="w-full border-collapse text-[13.5px]">
                  <thead>
                    <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
                      <th className="px-4 py-2.5">Cod BD MEDOC</th>
                      <th className="px-4 py-2.5">Cod MEDOC</th>
                      <th className="px-4 py-2.5">Denumire MEDOC</th>
                      <th className="px-4 py-2.5">Adăugat</th>
                      {canEdit && <th className="px-4 py-2.5 text-right">Acțiuni</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <RowSkeleton colCount={colCount} />
                    <RowSkeleton colCount={colCount} />
                    <RowSkeleton colCount={colCount} />
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!isError && !isPending && items.length === 0 && (
            <p className="text-[13.5px] text-ink-3">
              {hasAppliedFilters ? 'Niciun rezultat.' : 'Nomenclatorul este gol.'}
            </p>
          )}

          {!isError && !isPending && items.length > 0 && (
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <table className="w-full border-collapse text-[13.5px]">
                  <thead>
                    <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
                      <th className="px-4 py-2.5">Cod BD MEDOC</th>
                      <th className="px-4 py-2.5">Cod MEDOC</th>
                      <th className="px-4 py-2.5">Denumire MEDOC</th>
                      <th className="px-4 py-2.5">Adăugat</th>
                      {canEdit && <th className="px-4 py-2.5 text-right">Acțiuni</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((entry) => {
                      const isEditing = editingId === entry.id;
                      const isConfirmingDelete = confirmDeleteId === entry.id;
                      const updatePending = updateMutation.isPending && updateMutation.variables?.id === entry.id;
                      const deletePending = deleteMutation.isPending && deleteMutation.variables === entry.id;
                      const deleteFailed = deleteMutation.isError && deleteMutation.variables === entry.id;

                      return (
                        <Fragment key={entry.id}>
                          <tr className="border-b border-rule-soft align-top hover:bg-wash">
                            <td className="px-4 py-2">
                              {isEditing ? (
                                <Input
                                  aria-label="Cod BD MEDOC"
                                  autoFocus
                                  value={editFields.codBdMedoc}
                                  onChange={(e) => setEditFields((f) => ({ ...f, codBdMedoc: e.target.value }))}
                                  onKeyDown={(e) => handleEditKeyDown(e, entry.id)}
                                  className="h-8 w-full font-mono"
                                />
                              ) : (
                                <span className="font-mono text-[12.5px] text-ink-2">{entry.codBdMedoc}</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {isEditing ? (
                                <Input
                                  aria-label="Cod MEDOC"
                                  value={editFields.codMedoc}
                                  onChange={(e) => setEditFields((f) => ({ ...f, codMedoc: e.target.value }))}
                                  onKeyDown={(e) => handleEditKeyDown(e, entry.id)}
                                  className="h-8 w-full font-mono"
                                />
                              ) : (
                                <span className="font-mono text-[12.5px] text-ink-2">{entry.codMedoc}</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {isEditing ? (
                                <Input
                                  aria-label="Denumire MEDOC"
                                  value={editFields.denumireMedoc}
                                  onChange={(e) => setEditFields((f) => ({ ...f, denumireMedoc: e.target.value }))}
                                  onKeyDown={(e) => handleEditKeyDown(e, entry.id)}
                                  className="h-8 w-full"
                                />
                              ) : (
                                <span className="text-[13.5px] text-ink">{entry.denumireMedoc}</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 tabular-nums text-ink-3">
                              {entry.createdAt ? formatDateRo(new Date(entry.createdAt)) : '—'}
                            </td>
                            {canEdit && (
                              <td className="px-4 py-2 text-right">
                                {isEditing ? (
                                  <span className="inline-flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => void submitEdit(entry.id)}
                                      disabled={updatePending}
                                      className="text-[13.5px] font-medium text-teal hover:underline disabled:opacity-50"
                                    >
                                      {updatePending ? 'Se salvează…' : 'Salvează'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEdit}
                                      className="text-[13.5px] font-medium text-ink-2 hover:text-ink"
                                    >
                                      Anulează
                                    </button>
                                  </span>
                                ) : isConfirmingDelete ? (
                                  <span className="inline-flex items-center gap-2">
                                    <span className="text-ink-3">Sigur?</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        deleteMutation.mutate(entry.id);
                                        setConfirmDeleteId(null);
                                      }}
                                      disabled={deletePending}
                                      className="text-[13.5px] font-medium text-bad hover:underline disabled:opacity-50"
                                    >
                                      Da, șterge
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmDeleteId(null)}
                                      className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
                                    >
                                      Nu
                                    </button>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => startEdit(entry)}
                                      title="Editează"
                                      aria-label={`Editează ${entry.denumireMedoc}`}
                                      className="flex size-7 items-center justify-center rounded text-ink-3 transition-colors hover:text-teal"
                                    >
                                      <Pencil className="size-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmDeleteId(entry.id)}
                                      title="Șterge"
                                      aria-label={`Șterge ${entry.denumireMedoc}`}
                                      className="flex size-7 items-center justify-center rounded text-ink-3 transition-colors hover:text-bad"
                                    >
                                      <Trash2 className="size-4" />
                                    </button>
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                          {isEditing && editError && (
                            <tr className="border-b border-rule-soft">
                              <td colSpan={colCount} className="px-4 pb-2 pt-0 text-[12.5px] text-bad">
                                {editError}
                              </td>
                            </tr>
                          )}
                          {!isEditing && deleteFailed && (
                            <tr className="border-b border-rule-soft">
                              <td colSpan={colCount} className="px-4 pb-2 pt-0 text-[12.5px] text-bad">
                                Ștergere eșuată.
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {!isError && !isPending && total > 0 && (
          <div className="mt-4 flex items-center justify-between text-[13.5px] text-ink-3">
            <span className="tabular-nums">
              <span className="font-semibold text-ink">{from}–{to}</span> din{' '}
              <span className="font-semibold text-ink">{formatNumberRo(total)}</span>
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                title="Pagina anterioară"
                aria-label="Pagina anterioară"
                className="flex size-8 items-center justify-center rounded text-ink-2 transition-colors hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="tabular-nums">Pagina {page} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                title="Pagina următoare"
                aria-label="Pagina următoare"
                className="flex size-8 items-center justify-center rounded text-ink-2 transition-colors hover:text-ink disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
